import { supabase } from '../config/supabase';

export type FinancialMigrationMode = 'off' | 'read' | 'write';
export type FinancialMigrationStatus = 'planned' | 'enabled' | 'paused' | 'rolled_back';

export interface FinancialMigrationControl {
  exists: boolean;
  controlId?: string;
  clientId: string;
  stage: string;
  mode: FinancialMigrationMode;
  status: FinancialMigrationStatus;
  enabledAt: string | null;
  disabledAt: string | null;
  actorUserId?: string | null;
  notes: string | null;
  updatedAt?: string | null;
}

function normalizeControl(data: unknown): FinancialMigrationControl {
  if (!data || typeof data !== 'object') {
    throw new Error('O controle de migração retornou um formato inválido.');
  }

  const value = data as Record<string, unknown>;
  if (typeof value.clientId !== 'string' || typeof value.stage !== 'string'
    || !['off', 'read', 'write'].includes(String(value.mode))
    || !['planned', 'enabled', 'paused', 'rolled_back'].includes(String(value.status))) {
    throw new Error('O controle de migração retornou um estado inválido.');
  }

  return {
    exists: value.exists === true,
    controlId: typeof value.controlId === 'string' ? value.controlId : undefined,
    clientId: value.clientId,
    stage: value.stage,
    mode: value.mode as FinancialMigrationMode,
    status: value.status as FinancialMigrationStatus,
    enabledAt: typeof value.enabledAt === 'string' ? value.enabledAt : null,
    disabledAt: typeof value.disabledAt === 'string' ? value.disabledAt : null,
    actorUserId: typeof value.actorUserId === 'string' ? value.actorUserId : null,
    notes: typeof value.notes === 'string' ? value.notes : null,
    updatedAt: typeof value.updatedAt === 'string' ? value.updatedAt : null,
  };
}

function migrationControlError(error: { code?: string; message?: string }): Error {
  if (error.code === '42501') return new Error('Seu acesso não permite consultar o rollout deste tenant.');
  if (error.code === 'PGRST202' || error.code === '42883') {
    return new Error('O controle de migração ainda não foi aplicado no banco. Execute o 27b.');
  }
  return new Error(error.message || 'Não foi possível consultar o controle de migração.');
}

export async function readFinancialMigrationControl(
  clientId: string,
  stage = 'a8',
): Promise<FinancialMigrationControl> {
  if (!clientId) throw new Error('Tenant é obrigatório para consultar o rollout.');

  const { data, error } = await supabase.rpc('financial_a8_read_migration_control', {
    p_client_id: clientId,
    p_stage: stage,
  });
  if (error) throw migrationControlError(error);
  return normalizeControl(data);
}

export async function setFinancialMigrationControl(input: {
  clientId: string;
  stage?: string;
  mode: FinancialMigrationMode;
  status: FinancialMigrationStatus;
  notes?: string | null;
}): Promise<FinancialMigrationControl> {
  if (!input.clientId) throw new Error('Tenant é obrigatório para alterar o rollout.');

  const { data, error } = await supabase.rpc('financial_a8_set_migration_control', {
    p_client_id: input.clientId,
    p_stage: input.stage ?? 'a8',
    p_mode: input.mode,
    p_status: input.status,
    p_notes: input.notes ?? null,
  });
  if (error) throw migrationControlError(error);
  return normalizeControl(data);
}
